/**
 * useMaintenanceIssues — list maintenance_issues visible to the current user.
 *
 * RLS does the visibility scoping:
 *   - super_admin sees every org
 *   - supervisor / contractor_admin see all in their org
 *   - maintenance sees issues assigned to them OR reported by them in
 *     their org
 *   - cleaner has no maintenance_issues SELECT policy (this hook returns
 *     empty for them — the dashboard is gated by role)
 *
 * Default filter: not yet resolved or closed (status IN open / assigned /
 * in_progress). The maintenance dashboard surfaces actionable work; the
 * historical view comes later.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export type MaintenanceIssueStatus =
  | 'open'
  | 'assigned'
  | 'in_progress'
  | 'resolved'
  | 'closed'

export type MaintenanceSeverity = 'low' | 'medium' | 'high' | 'critical'

export type AiTriageStatus = 'pending' | 'suggested' | 'unavailable' | 'malformed'

export interface MaintenanceIssueRow {
  id:                  string
  room_id:             string
  title:               string
  description:         string | null
  severity:            MaintenanceSeverity
  status:              MaintenanceIssueStatus
  blocks_readiness:    boolean
  opened_at:           string
  resolved_at:         string | null
  resolution_notes:    string | null
  assigned_to_user_id: string | null
  reported_by_user_id: string
  // AI triage suggestion columns (PROJECT_STATE task #66). Populated by
  // the triage-maintenance-issue Edge Function for new issues; NULL on
  // rows that pre-date the migration. The dashboard renders an
  // "AI suggests" banner when ai_triage_status === 'suggested' and
  // ai_triage_reviewed_at IS NULL.
  ai_triage_status:               AiTriageStatus | null
  ai_suggested_severity:          MaintenanceSeverity | null
  ai_suggested_category_id:       string | null
  ai_suggested_is_blocking:       boolean | null
  ai_triage_reason:               string | null
  ai_triage_reviewed_at:          string | null
  ai_triage_reviewed_by_user_id:  string | null
  ai_suggested_category: {
    id:   string
    name: string
  } | null
  room: {
    id:          string
    room_number: string
    building:    { id: string; name: string } | null
  } | null
}

const ACTIONABLE: readonly MaintenanceIssueStatus[] = ['open', 'assigned', 'in_progress']

export function useMaintenanceIssues(): UseQueryResult<MaintenanceIssueRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['maintenance-issues', 'actionable'],
    queryFn: async (): Promise<MaintenanceIssueRow[]> => {
      const { data, error } = await supabase
        .from('maintenance_issues')
        .select(
          'id, room_id, title, description, severity, status, blocks_readiness,' +
          ' opened_at, resolved_at, resolution_notes, assigned_to_user_id, reported_by_user_id,' +
          ' ai_triage_status, ai_suggested_severity, ai_suggested_category_id,' +
          ' ai_suggested_is_blocking, ai_triage_reason, ai_triage_reviewed_at,' +
          ' ai_triage_reviewed_by_user_id,' +
          ' ai_suggested_category:maintenance_categories!ai_suggested_category_id(id, name),' +
          ' room:rooms(id, room_number, building:buildings(id, name))',
        )
        .in('status', ACTIONABLE)
        .order('opened_at', { ascending: true })
        .limit(500)

      if (error) throw error
      return (data ?? []) as unknown as MaintenanceIssueRow[]
    },
    refetchInterval: 60_000,
  })
}

/**
 * Severity rank used for ordering / urgency display. Pinned constant so
 * the order is one decision in one place.
 */
export const SEVERITY_RANK: Readonly<Record<MaintenanceSeverity, number>> = {
  critical: 0,
  high:     1,
  medium:   2,
  low:      3,
}
